import { defaultVertex, defaultVertexRaw } from "@/mono/lib/gl/shaderChunks";
import { mouseState } from "@/mono/lib/hooks/useMouseMove";
import { useGlobalStore } from "@/mono/store/global";
import { useFrame, useThree } from "@react-three/fiber";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { FrontSide, PlaneGeometry, Vector2 } from "three";
import CustomShaderMaterial from "three-custom-shader-material";

const defaultFragmentShader = /*glsl*/ `
    varying vec2 vUv;

    void main() {
        gl_FragColor = vec4(vUv, 1., 1.);
    }
`;

function ShaderObjectImpl(
  {
    name = "ShaderObject",
    vertexShader: _vertexShader,
    fragmentShader = defaultFragmentShader,
    uniforms = {},
    geometry = new PlaneGeometry(),
    isRaw = false,
    transparent = true,

    side = FrontSide,
    onBeforeCompile = () => {},
    visible: _visible = false,
    defines = {},

    useMouse = false,
    mouseLerpAlpha = 0.1,

    baseMaterial = null,

    onLoop = () => {},

    children,

    wireframe = false,

    spectorData = {},

    ...props
  },
  ref
) {
  const localRef = useRef();
  const visible = useRef(_visible);
  const vertexShader = useMemo(
    () => _vertexShader ?? (isRaw ? defaultVertexRaw : defaultVertex),
    [isRaw]
  );

  const _mouse = useMemo(() => new Vector2(), []);

  const [{ dpr }] = useThree((s) => [s.viewport]);
  const windowSize = useGlobalStore((s) => s.windowSize);

  const shader = useMemo(
    () => ({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: {
          value: 0,
        },
        uMouse: {
          value: _mouse,
        },
        uResolution: {
          value: new Vector2(windowSize.width * dpr, windowSize.height * dpr),
        },
        ...uniforms,
      },
      side,
      transparent,
      wireframe,
      // defines: baseMaterial ? {} : defines,
      onBeforeCompile: (shader) => {
        shader.__SPECTOR_Metadata = {
          name,
          ...spectorData,
        };
        onBeforeCompile?.(shader);
      },
    }),
    []
  );

  useEffect(() => {
    shader.uniforms.uResolution.value.set(
      windowSize.width * dpr,
      windowSize.height * dpr
    );
  }, [windowSize]);

  useImperativeHandle(ref, () => ({
    shader,
    mesh: localRef.current,
    isVisible: () => visible.current,
    toggleVisible: (v) => {
      visible.current = v;
      if (localRef.current) localRef.current.visible = v;
    },
    set: (uniform, value) => {
      if (!shader.uniforms[uniform]) {
        console.warn("Uniform not found", uniform);
        return;
      }
      shader.uniforms[uniform].value = value;
    },
    get: (uniform) => {
      return shader.uniforms[uniform].value;
    },
    // Not necessary in R3F
    dispose: () => {
      // visible.current = false;
      // if (!localRef.current) return;
      // localRef.current.visible = false;
      // localRef.current?.geometry?.dispose?.();
      // localRef.current?.material?.dispose?.();
      // localRef.current?.dispose?.();
      // localRef.current = null;
    },
  }));

  useFrame((state, delta) => {
    if (!visible.current) return;

    if (useMouse) {
      _mouse.lerp(mouseState.getState().mouseNormalized, mouseLerpAlpha);
      shader.uniforms.uMouse.value = _mouse;
    }

    shader.uniforms.uTime.value += delta;

    onLoop?.(state, delta);
  });

  const _geometry = useMemo(() => {
    if (children) return null;
    return geometry;
  }, [children]);

  return (
    <mesh
      ref={localRef}
      visible={visible.current}
      geometry={_geometry}
      {...props}
    >
      {children}
      {baseMaterial ? (
        <CustomShaderMaterial silent baseMaterial={baseMaterial} {...shader} />
      ) : (
        <shaderMaterial {...shader} />
      )}
    </mesh>
  );
}

const ShaderObject = forwardRef(ShaderObjectImpl);
export default ShaderObject;
